import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Box,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { Line, LineCreate, LineUpdate } from '../types/line';

interface LineFormData {
  name: string;
  description?: string;
  status: boolean;
}

interface LineFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: LineCreate | LineUpdate) => Promise<void>;
  projectId: number;
  line?: Line;
}

export const LineFormDialog: React.FC<LineFormDialogProps> = ({
  open,
  onClose,
  onSubmit,
  projectId,
  line,
}) => {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<LineFormData>({
    defaultValues: {
      name: line?.name || '',
      description: line?.description || '',
      status: line?.status ?? true,
    },
  });

  React.useEffect(() => {
    if (open) {
      reset({
        name: line?.name || '',
        description: line?.description || '',
        status: line?.status ?? true,
      });
    }
  }, [open, line, reset]);

  const handleFormSubmit = async (data: LineFormData) => {
    try {
      await onSubmit({
        ...data,
        project_id: projectId,
      });
      onClose();
    } catch (error) {
      // Error will be handled by the store
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{line ? 'Edit Line' : 'Create Line'}</DialogTitle>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Controller
              name="name"
              control={control}
              rules={{ required: 'Name is required' }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Name"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  fullWidth
                />
              )}
            />

            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Description"
                  multiline
                  rows={4}
                  fullWidth
                />
              )}
            />

            <Controller
              name="status"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={value}
                      onChange={(e) => onChange(e.target.checked)}
                    />
                  }
                  label="Active"
                />
              )}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={isSubmitting}
          >
            {line ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
